import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { DISPLAY_NAME_COLUMN_ID, TOTAL_COLUMN_SUFFIX } from '../../BalanceSheetTable/BalanceSheetTable.columns';
import { BalanceSheetConfigColumn } from '../BalanceSheetConfigPanel.types';

/**
 * Filters out non-persistable column IDs (display name, totals, internal
 * prefixed columns) to produce the column order array for the save mutation.
 */
export function filterColumnIdsForSave(columnIds: string[]): string[] {
  return columnIds.filter(id => {
    return id !== DISPLAY_NAME_COLUMN_ID && !id.endsWith(TOTAL_COLUMN_SUFFIX) && !id.startsWith('__');
  });
}
interface UseDraftColumnOrderArgs {
  inEstateColumns: BalanceSheetConfigColumn[];
  outOfEstateColumns: BalanceSheetConfigColumn[];
  inEstateTotalColumn: BalanceSheetConfigColumn | null;
  outOfEstateTotalColumn: BalanceSheetConfigColumn | null;
  setDraftColumnOrder: (order: string[] | null) => void;
}
export interface UseDraftColumnOrderReturn {
  orderedInEstate: BalanceSheetConfigColumn[];
  orderedOutOfEstate: BalanceSheetConfigColumn[];
  handleInEstateDropped: (items: BalanceSheetConfigColumn[]) => void;
  handleOutOfEstateDropped: (items: BalanceSheetConfigColumn[]) => void;
  isDirty: boolean;
  getColumnOrderForSave: () => string[];
  commitSnapshot: () => void;
  reinitialize: () => void;
  clearDraft: () => void;
}
export function useDraftColumnOrder(t0) {
  const $ = _c(29);
  const {
    inEstateColumns,
    outOfEstateColumns,
    inEstateTotalColumn,
    outOfEstateTotalColumn,
    setDraftColumnOrder
  } = t0;
  const orderedInEstateRef = useRef(inEstateColumns);
  const orderedOutOfEstateRef = useRef(outOfEstateColumns);
  const [orderedInEstate, setOrderedInEstate] = useState(inEstateColumns);
  const [orderedOutOfEstate, setOrderedOutOfEstate] = useState(outOfEstateColumns);
  const [columnOrderDirty, setColumnOrderDirty] = useState(false);
  let t1;
  let t2;
  if ($[0] !== inEstateColumns || $[1] !== outOfEstateColumns || $[2] !== setDraftColumnOrder) {
    t1 = () => {
      orderedInEstateRef.current = inEstateColumns;
      orderedOutOfEstateRef.current = outOfEstateColumns;
      setOrderedInEstate(inEstateColumns);
      setOrderedOutOfEstate(outOfEstateColumns);
      setDraftColumnOrder(null);
      setColumnOrderDirty(false);
    };
    t2 = [inEstateColumns, outOfEstateColumns, setDraftColumnOrder];
    $[0] = inEstateColumns;
    $[1] = outOfEstateColumns;
    $[2] = setDraftColumnOrder;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  let t3;
  if ($[5] !== inEstateTotalColumn || $[6] !== outOfEstateTotalColumn || $[7] !== setDraftColumnOrder) {
    t3 = () => {
      const order = [...orderedInEstateRef.current.map(_temp), ...(inEstateTotalColumn ? [inEstateTotalColumn.id] : []), ...orderedOutOfEstateRef.current.map(_temp2), ...(outOfEstateTotalColumn ? [outOfEstateTotalColumn.id] : [])];
      setDraftColumnOrder(order);
    };
    $[5] = inEstateTotalColumn;
    $[6] = outOfEstateTotalColumn;
    $[7] = setDraftColumnOrder;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const pushDraftOrder = t3;
  let t4;
  if ($[9] !== pushDraftOrder) {
    t4 = sortedItems => {
      orderedInEstateRef.current = sortedItems;
      setOrderedInEstate(sortedItems);
      setColumnOrderDirty(true);
      pushDraftOrder();
    };
    $[9] = pushDraftOrder;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const handleInEstateDropped = t4;
  let t5;
  if ($[11] !== pushDraftOrder) {
    t5 = sortedItems_0 => {
      orderedOutOfEstateRef.current = sortedItems_0;
      setOrderedOutOfEstate(sortedItems_0);
      setColumnOrderDirty(true);
      pushDraftOrder();
    };
    $[11] = pushDraftOrder;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const handleOutOfEstateDropped = t5;
  let t6;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => filterColumnIdsForSave([...orderedInEstateRef.current.map(_temp3), ...orderedOutOfEstateRef.current.map(_temp4)]);
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const getColumnOrderForSave = t6;
  let t7;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      setColumnOrderDirty(false);
    };
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const commitSnapshot = t7;
  let t8;
  if ($[15] !== inEstateColumns || $[16] !== outOfEstateColumns || $[17] !== setDraftColumnOrder) {
    t8 = () => {
      orderedInEstateRef.current = inEstateColumns;
      orderedOutOfEstateRef.current = outOfEstateColumns;
      setOrderedInEstate(inEstateColumns);
      setOrderedOutOfEstate(outOfEstateColumns);
      setColumnOrderDirty(false);
      setDraftColumnOrder(null);
    };
    $[15] = inEstateColumns;
    $[16] = outOfEstateColumns;
    $[17] = setDraftColumnOrder;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const reinitialize = t8;
  let t9;
  if ($[19] !== setDraftColumnOrder) {
    t9 = () => {
      setDraftColumnOrder(null);
      setColumnOrderDirty(false);
    };
    $[19] = setDraftColumnOrder;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const clearDraft = t9;
  let t10;
  if ($[21] !== clearDraft || $[22] !== columnOrderDirty || $[23] !== handleInEstateDropped || $[24] !== handleOutOfEstateDropped || $[25] !== orderedInEstate || $[26] !== orderedOutOfEstate || $[27] !== reinitialize) {
    t10 = {
      orderedInEstate,
      orderedOutOfEstate,
      handleInEstateDropped,
      handleOutOfEstateDropped,
      isDirty: columnOrderDirty,
      getColumnOrderForSave,
      commitSnapshot,
      reinitialize,
      clearDraft
    };
    $[21] = clearDraft;
    $[22] = columnOrderDirty;
    $[23] = handleInEstateDropped;
    $[24] = handleOutOfEstateDropped;
    $[25] = orderedInEstate;
    $[26] = orderedOutOfEstate;
    $[27] = reinitialize;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  return t10;
}
function _temp4(col_2) {
  return col_2.id;
}
function _temp3(col_1) {
  return col_1.id;
}
function _temp2(col_0) {
  return col_0.id;
}
function _temp(col) {
  return col.id;
}